export const CF_SUB_CASES = ['proxied', 'on_cf_not_proxied', 'not_on_cf'] as const;

export type CfSubCase = (typeof CF_SUB_CASES)[number];

export type CfProbeResult = CfSubCase | 'already_integrated' | 'blocked' | 'error';

/**
 * Where the customer started the flow from. The server refuses a value outside
 * this set (`CF_ENTRY_SOURCES`, PRE-3283), because it is the `entry_source`
 * telemetry dimension on every CF beta event.
 */
export type CfEntrySource = 'wizard' | 'email';

export type CfBlockReason = 'other_cdn' | 'app_platform' | 'locked_nameservers';

export type CfProbeErrorReason =
  | 'unreachable'
  | 'nxdomain'
  | 'tls'
  | 'blocked_by_guard'
  /**
   * The site answered, and what it answered proves it is not serving content —
   * a 5xx, or a 404 at the apex. Distinct from `unreachable` because the site
   * *was* reached (PRE-3283).
   */
  | 'http_error';

export type CfDomainOwnership = 'current_account' | 'another_account';

/**
 * The screen a returning customer belongs on, from the server's
 * `resolveResumeScreen` (PRE-3275).
 *
 * Mirrored rather than imported: this is rest-api's `CfFlowScreen`, and the
 * dashboard cannot import from a service. Keep the two in step — a value the
 * server can send and this union does not name is a screen nobody routes to.
 */
export type CfFlowScreen =
  | 'blocked'
  | 'cf_account'
  | 'token'
  | 'reconnect'
  | 'nameservers'
  | 'propagation'
  | 'progress'
  | 'error'
  | 'success'
  | 'abandoned';

export type CfProbeResponse = {
  result: CfProbeResult;
  subCase: CfSubCase | null;
  blockReason: CfBlockReason | null;
  /** Telemetry only; no screen renders it — see rest-api's `ProbeDomainResponseDto`. */
  detectedSignal: string | null;
  errorReason: CfProbeErrorReason | null;
  /** The normalised domain the flow is keyed on. */
  domain: string;
  /**
   * Set when a flow already exists and has moved past its opening screen.
   *
   * The closed union rather than `string`, so a consumer inherits the server's
   * vocabulary; `isTerminallyBlocked` and the wizard both read it.
   */
  resumeScreen: CfFlowScreen | null;
  /** Whether the probe left a flow the watched screens can follow. */
  flowOpened: boolean;
  ownership: CfDomainOwnership | null;
};
